// Email send view (audit #11) — MVP one-off compose + history.
// Drip sequences come in a follow-up build; this is the minimum viable
// "I can email a client from inside the CRM" surface.
import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { Send, Mail } from 'lucide-react';
import { supabase } from '../lib/supabase';

async function apiFetch(path, opts = {}) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...opts,
    headers: { 'content-type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}), ...(opts.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  return body;
}

export default function EmailComposeView({ currentUser, showToast = () => {} }) {
  // Prefill from inbox → "Ad-hoc email" pass-through, one-shot via
  // sessionStorage so a refresh doesn't strand the address on the
  // page after the operator navigates away.
  const [to, setTo] = React.useState(() => {
    try {
      const pref = sessionStorage.getItem('email.prefillTo');
      if (pref) {
        sessionStorage.removeItem('email.prefillTo');
        return pref;
      }
    } catch {}
    return '';
  });
  const [subject, setSubject] = React.useState('');
  const [body, setBody]       = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [recent, setRecent]   = React.useState([]);

  const loadRecent = React.useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      // Direct DB read since /api/email/list isn't built; RLS scopes by owner.
      const { data } = await supabase.from('email_messages').select('id, to_email, subject, status, created_at, error').order('created_at', { ascending: false }).limit(20);
      setRecent(data || []);
    } catch { /* ignore */ }
  }, []);
  React.useEffect(() => { loadRecent(); }, [loadRecent]);

  const send = async () => {
    if (sending) return;
    if (!to.trim() || !subject.trim() || !body.trim()) { showToast('To / Subject / Body all required.'); return; }
    setSending(true);
    try {
      const r = await apiFetch('/api/email/send', {
        method: 'POST',
        body: JSON.stringify({ to_email: to.trim(), subject: subject.trim(), body_text: body }),
      });
      if (r.delivered) showToast('Email sent.');
      else            showToast(r.note || 'Email logged (provider not configured).');
      setSubject(''); setBody('');
      loadRecent();
    } catch (err) {
      showToast(`Send failed: ${err.message}`);
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-br from-slate-900 to-emerald-900 text-white rounded-xl p-5">
        <div className="text-emerald-300 text-[0.55rem] font-black uppercase tracking-[0.3em]">Email · Outbound</div>
        <h1 className="text-xl font-bold mt-1">Send a quick email</h1>
        <p className="text-slate-300 text-sm mt-2">MVP send-one-email surface. Drip sequences ship next. Sends via Resend when RESEND_API_KEY is set on Vercel — otherwise logs to the message ledger so you can see what would have gone.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2">
        <input value={to} onChange={e => setTo(e.target.value)} placeholder="To (email address)" className="w-full text-sm border border-slate-300 rounded-md px-3 py-2"/>
        <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject" className="w-full text-sm border border-slate-300 rounded-md px-3 py-2"/>
        <textarea value={body} onChange={e => setBody(e.target.value)} rows={8} placeholder="Body" className="w-full text-sm border border-slate-300 rounded-md px-3 py-2"/>
        <div className="flex justify-end">
          <button onClick={send} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-bold px-4 py-2 rounded-md inline-flex items-center gap-1.5"><Send size={14}/> {sending ? 'Sending…' : 'Send'}</button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-2 border-b border-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5"><Mail size={11}/> Recent (20)</div>
        {recent.length === 0 ? <div className="px-4 py-3 text-sm text-slate-500 italic">No emails yet.</div> :
         <div className="divide-y divide-slate-100">
           {recent.map(m => (
             <div key={m.id} className="px-4 py-2">
               <div className="text-sm font-bold text-slate-900 flex items-center gap-2">
                 {m.subject}
                 <span className={`text-[10px] font-bold uppercase tracking-wider border px-1.5 py-0.5 rounded ${m.status === 'sent' || m.status === 'delivered' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : m.status === 'failed' || m.status === 'bounced' ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-slate-50 text-slate-700 border-slate-200'}`}>{m.status}</span>
               </div>
               <div className="text-xs text-slate-500 mt-0.5">{m.to_email} · {new Date(m.created_at).toLocaleString()}</div>
               {m.error && <div className="text-[11px] text-rose-700 mt-0.5">{m.error}</div>}
             </div>
           ))}
         </div>}
      </div>
    </div>
  );
}
